import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Spinner, Table } from "react-bootstrap";
import { FaCalendarPlus, FaClone, FaPlus, FaRedo, FaTrash } from "react-icons/fa";
import api from "../api";
import { useUser } from "../contextProviders/userContext";
import "./ProjectFormManager.css";
import RepeatSessionsPanel from "./RepeatSessionsPanel";
import {
  formatSouthAfricaDateTime,
  formatSouthAfricaDateTimeLocal,
  SOUTH_AFRICA_TIME_LABEL,
  parseSouthAfricaDateTimeLocal,
} from "../utils/projectFormDateTime";

// Mirrors the backend limit in projectFormService.js.
const MAX_FORM_FIELDS = 30;
const FORM_DRAFT_VERSION = 1;
const FORM_DRAFT_KEY_PREFIX = "wcco:project-form-draft";

const fieldTypes = [
  ["information", "Information or link"],
  ["short_text", "Short answer"],
  ["long_text", "Long answer"],
  ["single_choice", "Choose one"],
  ["multi_choice", "Choose multiple"],
  ["availability", "Availability slots"],
];

const clientId = () =>
  `field-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const blankSlot = () => ({
  _key: clientId(),
  activity: "",
  label: "",
  startTime: "",
  endTime: "",
  capacity: 0,
  location: "",
});

const toLocalInput = (value) => {
  if (!value) return "";
  return formatSouthAfricaDateTimeLocal(value);
};

const blankForm = () => ({
  title: "Volunteer availability",
  description: "Select every session you are available to attend.",
  status: "draft",
  closesAt: "",
  fields: [
    {
      _key: clientId(),
      label: "Choose your available sessions",
      type: "availability",
      helpText: "Select every date and activity you can attend.",
      required: true,
      optionsText: "",
      slots: [blankSlot()],
    },
  ],
});

const suggestSlotEndTime = (startValue) => {
  const start = parseSouthAfricaDateTimeLocal(startValue);
  if (!start) return "";
  return formatSouthAfricaDateTimeLocal(
    new Date(start.getTime() + 60 * 60 * 1000),
  );
};

const slotHasInvalidRange = (slot) => {
  if (!slot.startTime || !slot.endTime) return false;
  const start = parseSouthAfricaDateTimeLocal(slot.startTime);
  const end = parseSouthAfricaDateTimeLocal(slot.endTime);
  return Boolean(start && end && end <= start);
};

const validateEditor = (form) => {
  if (!form.title?.trim()) return "Enter a form title.";
  if (!form.fields?.length)
    return "Add at least one question before saving the form.";
  if (form.closesAt && !parseSouthAfricaDateTimeLocal(form.closesAt)) {
    return "Enter a valid closing date.";
  }

  for (let fieldIndex = 0; fieldIndex < form.fields.length; fieldIndex += 1) {
    const field = form.fields[fieldIndex];
    const question = field.label?.trim() || `Question ${fieldIndex + 1}`;
    if (!field.label?.trim())
      return `Question ${fieldIndex + 1} needs a title.`;

    if (["single_choice", "multi_choice"].includes(field.type)) {
      const options = (field.optionsText || "")
        .split("\n")
        .map((option) => option.trim())
        .filter(Boolean);
      if (options.length < 2)
        return `“${question}” needs at least two options.`;
    }

    if (field.type === "availability") {
      if (!field.slots?.length)
        return `Add at least one time slot to “${question}”.`;
      for (let slotIndex = 0; slotIndex < field.slots.length; slotIndex += 1) {
        const slot = field.slots[slotIndex];
        if (!slot.activity?.trim() || !slot.startTime || !slot.endTime) {
          return `Complete the activity, start, and end for slot ${slotIndex + 1} in “${question}”.`;
        }
        const startTime = parseSouthAfricaDateTimeLocal(slot.startTime);
        const endTime = parseSouthAfricaDateTimeLocal(slot.endTime);
        if (!startTime || !endTime || endTime <= startTime) {
          return `Slot ${slotIndex + 1} in “${question}” must end after it starts.`;
        }
      }
    }
  }
  return "";
};

const prepareForm = (form) => ({
  ...form,
  closesAt: toLocalInput(form.closesAt),
  fields: (form.fields || []).map((field) => ({
    ...field,
    _key: field._id || clientId(),
    optionsText: (field.options || []).length
      ? field.options.map((option) => option.label).join("\n")
      : ["single_choice", "multi_choice"].includes(field.type)
        ? "Option 1\nOption 2"
        : "",
    slots: (field.slots || []).map((slot) => ({
      ...slot,
      _key: slot._id || clientId(),
      startTime: toLocalInput(slot.startTime),
      endTime: toLocalInput(slot.endTime),
    })),
  })),
});

const ProjectFormManager = ({ projectId }) => {
  const { user } = useUser();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [newFieldType, setNewFieldType] = useState("availability");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [builderError, setBuilderError] = useState("");
  const [message, setMessage] = useState("");
  const [responseData, setResponseData] = useState(null);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [hasSavedDraft, setHasSavedDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [repeatFieldKey, setRepeatFieldKey] = useState("");
  const userId = user?._id || user?.id || "anonymous";
  const draftKey = useMemo(
    () => (projectId ? `${FORM_DRAFT_KEY_PREFIX}:${userId}:${projectId}` : ""),
    [projectId, userId],
  );

  const persistEditorDraft = useCallback(
    (value) => {
      if (!draftKey || !value) return false;
      try {
        const savedAt = Date.now();
        localStorage.setItem(
          draftKey,
          JSON.stringify({
            version: FORM_DRAFT_VERSION,
            projectId,
            savedAt,
            editor: value,
          }),
        );
        setHasSavedDraft(true);
        setDraftSavedAt(savedAt);
        return true;
      } catch (storageError) {
        console.error("Unable to save form draft:", storageError);
        setBuilderError("This form draft could not be saved on this device.");
        return false;
      }
    },
    [draftKey, projectId],
  );

  const clearEditorDraft = useCallback(() => {
    if (draftKey) localStorage.removeItem(draftKey);
    setHasSavedDraft(false);
    setDraftSavedAt(null);
  }, [draftKey]);

  const restoreEditorDraft = useCallback(
    (showMessage = true) => {
      if (!draftKey) return false;
      try {
        const stored = JSON.parse(localStorage.getItem(draftKey) || "null");
        if (
          stored?.version !== FORM_DRAFT_VERSION ||
          stored?.projectId !== projectId ||
          !stored?.editor?.fields
        )
          return false;
        setEditor(stored.editor);
        setResponseData(null);
        setHasSavedDraft(true);
        setDraftSavedAt(stored.savedAt || null);
        setBuilderError("");
        if (showMessage)
          setMessage("Your unsaved form draft was restored from this device.");
        return true;
      } catch (storageError) {
        console.error("Unable to restore form draft:", storageError);
        localStorage.removeItem(draftKey);
        setHasSavedDraft(false);
        return false;
      }
    },
    [draftKey, projectId],
  );

  const loadForms = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const response = await api.get(`/projects/${projectId}/forms`);
      setForms(response.data || []);
    } catch (requestError) {
      setError(
        requestError.response?.data?.error || "Unable to load project forms.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    setEditor(null);
    setHasSavedDraft(false);
    setDraftSavedAt(null);
    restoreEditorDraft();
  }, [restoreEditorDraft]);

  useEffect(() => {
    if (!editor) return undefined;
    const saveTimer = window.setTimeout(() => persistEditorDraft(editor), 350);
    return () => window.clearTimeout(saveTimer);
  }, [editor, persistEditorDraft]);

  useEffect(() => {
    if (!editor) return undefined;
    const flushDraft = () => persistEditorDraft(editor);
    const flushHiddenDraft = () => {
      if (document.visibilityState === "hidden") flushDraft();
    };
    window.addEventListener("pagehide", flushDraft);
    window.addEventListener("beforeunload", flushDraft);
    document.addEventListener("visibilitychange", flushHiddenDraft);
    return () => {
      window.removeEventListener("pagehide", flushDraft);
      window.removeEventListener("beforeunload", flushDraft);
      document.removeEventListener("visibilitychange", flushHiddenDraft);
    };
  }, [editor, persistEditorDraft]);

  const startNewForm = () => {
    if (
      (editor || hasSavedDraft) &&
      !window.confirm("Replace the form draft saved on this device?")
    )
      return;
    clearEditorDraft();
    setEditor(prepareForm(blankForm()));
    setResponseData(null);
    setBuilderError("");
    setMessage("");
  };

  const openFormEditor = (form) => {
    if (
      (editor || hasSavedDraft) &&
      !window.confirm("Replace the form draft saved on this device?")
    )
      return;
    clearEditorDraft();
    setEditor(prepareForm(form));
    setResponseData(null);
    setBuilderError("");
  };

  const closeEditor = () => {
    if (editor && persistEditorDraft(editor)) {
      setMessage("Form draft saved on this device. You can resume it later.");
    }
    setEditor(null);
  };

  const discardEditorDraft = () => {
    if (!window.confirm("Discard this unsaved form draft?")) return;
    clearEditorDraft();
    setEditor(null);
    setBuilderError("");
    setMessage("Unsaved form draft discarded.");
  };

  const updateField = (index, changes) =>
    setEditor((current) => ({
      ...current,
      fields: current.fields.map((field, fieldIndex) =>
        fieldIndex === index ? { ...field, ...changes } : field,
      ),
    }));

  const changeFieldType = (fieldIndex, type) => {
    const field = editor.fields[fieldIndex];
    updateField(fieldIndex, {
      type,
      ...(["single_choice", "multi_choice"].includes(type) &&
      !field.optionsText?.trim()
        ? { optionsText: "Option 1\nOption 2" }
        : {}),
      ...(type === "availability" && !field.slots?.length
        ? { slots: [blankSlot()] }
        : {}),
    });
    setBuilderError("");
  };

  const addField = () => {
    if (editor.fields.length >= MAX_FORM_FIELDS) {
      setBuilderError(
        `A form can have at most ${MAX_FORM_FIELDS} questions. To add more dates, use "Add available slot" on an existing availability question.`,
      );
      return;
    }
    const typeLabel = fieldTypes.find(([value]) => value === newFieldType)?.[1];
    setEditor((current) => ({
      ...current,
      fields: [
        ...current.fields,
        {
          _key: clientId(),
          label:
            newFieldType === "availability"
              ? "Choose your available sessions"
              : typeLabel,
          type: newFieldType,
          helpText: "",
          required: false,
          optionsText: "Option 1\nOption 2",
          slots: newFieldType === "availability" ? [blankSlot()] : [],
        },
      ],
    }));
    setBuilderError("");
  };

  const addSlot = (fieldIndex) => {
    const field = editor.fields[fieldIndex];
    updateField(fieldIndex, {
      slots: [...(field.slots || []), blankSlot()],
    });
    setBuilderError("");
  };

  const addAvailableSlot = () => {
    const availabilityIndex = editor.fields.findIndex(
      (field) => field.type === "availability",
    );
    if (availabilityIndex >= 0) {
      addSlot(availabilityIndex);
      return;
    }

    setEditor((current) => ({
      ...current,
      fields: [
        ...current.fields,
        {
          _key: clientId(),
          label: "Choose your available sessions",
          type: "availability",
          helpText: "Select every date and activity you can attend.",
          required: true,
          optionsText: "",
          slots: [blankSlot()],
        },
      ],
    }));
    setBuilderError("");
  };

  const getChoiceOptions = (field) => {
    if (!field.optionsText) return [];
    return field.optionsText.split("\n");
  };

  const addChoice = (fieldIndex) => {
    const options = getChoiceOptions(editor.fields[fieldIndex]);
    updateField(fieldIndex, {
      optionsText: [...options, `Option ${options.length + 1}`].join("\n"),
    });
    setBuilderError("");
  };

  const updateChoice = (fieldIndex, optionIndex, value) => {
    const options = getChoiceOptions(editor.fields[fieldIndex]);
    options[optionIndex] = value;
    updateField(fieldIndex, { optionsText: options.join("\n") });
  };

  const removeChoice = (fieldIndex, optionIndex) => {
    const options = getChoiceOptions(editor.fields[fieldIndex]);
    updateField(fieldIndex, {
      optionsText: options
        .filter((_, index) => index !== optionIndex)
        .join("\n"),
    });
  };

  const addAnswerChoice = () => {
    const choiceIndex = editor.fields.findIndex((field) =>
      ["single_choice", "multi_choice"].includes(field.type),
    );
    if (choiceIndex >= 0) {
      addChoice(choiceIndex);
      return;
    }

    setEditor((current) => ({
      ...current,
      fields: [
        ...current.fields,
        {
          _key: clientId(),
          label: "Choose an option",
          type: "single_choice",
          helpText: "",
          required: false,
          optionsText: "Option 1\nOption 2",
          slots: [],
        },
      ],
    }));
    setBuilderError("");
  };

  const updateSlot = (fieldIndex, slotIndex, changes) => {
    const slots = editor.fields[fieldIndex].slots.map((slot, index) =>
      index === slotIndex ? { ...slot, ...changes } : slot,
    );
    updateField(fieldIndex, { slots });
  };

  const addSlots = (fieldIndex, newSlots) => {
    updateField(fieldIndex, {
      slots: [
        ...(editor.fields[fieldIndex].slots || []),
        ...newSlots.map((slot) => ({ ...slot, _key: clientId() })),
      ],
    });
    setBuilderError("");
  };

  // Same session a week later — the common "next one of these" case.
  const copySlotToNextWeek = (fieldIndex, slotIndex) => {
    const slot = editor.fields[fieldIndex].slots[slotIndex];
    const shift = (value) => {
      const date = parseSouthAfricaDateTimeLocal(value);
      return date
        ? formatSouthAfricaDateTimeLocal(new Date(date.getTime() + 7 * 24 * 60 * 60 * 1000))
        : "";
    };
    const copy = {
      ...slot,
      _id: undefined,
      _key: clientId(),
      startTime: shift(slot.startTime),
      endTime: shift(slot.endTime),
    };
    const slots = [...editor.fields[fieldIndex].slots];
    slots.splice(slotIndex + 1, 0, copy);
    updateField(fieldIndex, { slots });
  };

  const removeSlot = (fieldIndex, slotIndex) =>
    updateField(fieldIndex, {
      slots: editor.fields[fieldIndex].slots.filter(
        (_, index) => index !== slotIndex,
      ),
    });

  const saveForm = async (event) => {
    event.preventDefault();
    const validationError = validateEditor(editor);
    if (validationError) {
      setBuilderError(validationError);
      return;
    }
    setSaving(true);
    setError("");
    setBuilderError("");
    setMessage("");
    try {
      const payload = {
        title: editor.title,
        description: editor.description,
        status: editor.status,
        closesAt: editor.closesAt
          ? parseSouthAfricaDateTimeLocal(editor.closesAt)?.toISOString()
          : null,
        fields: editor.fields.map((field) => ({
          ...(field._id ? { _id: field._id } : {}),
          label: field.label,
          type: field.type,
          helpText: field.helpText,
          required: field.required,
          options: ["single_choice", "multi_choice"].includes(field.type)
            ? field.optionsText
                .split("\n")
                .map((label) => label.trim())
                .filter(Boolean)
                .map((label) => ({ label, value: label }))
            : [],
          slots:
            field.type === "availability"
              ? field.slots.map((slot) => ({
                  ...(slot._id ? { _id: slot._id } : {}),
                  activity: slot.activity,
                  label: slot.label,
                  startTime: parseSouthAfricaDateTimeLocal(
                    slot.startTime,
                  )?.toISOString(),
                  endTime: parseSouthAfricaDateTimeLocal(
                    slot.endTime,
                  )?.toISOString(),
                  capacity: Number(slot.capacity) || 0,
                  location: slot.location,
                }))
              : [],
        })),
      };
      const response = editor._id
        ? await api.patch(`/projects/${projectId}/forms/${editor._id}`, payload)
        : await api.post(`/projects/${projectId}/forms`, payload);
      const notificationJustSent =
        Boolean(response.data.notificationSentAt) && !editor.notificationSentAt;
      if (notificationJustSent) {
        setMessage(
          `${editor._id ? "Form updated" : "Form created"} and project participants notified.`,
        );
      } else if (response.data.status === "draft") {
        setMessage(
          `${editor._id ? "Form updated" : "Form created"} as a draft. Participants will be notified when it is published.`,
        );
      } else if (response.data.editNotification) {
        const { addedSessions, changedSessions, cancelledSessions } =
          response.data.editNotification;
        const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;
        const parts = [
          addedSessions && `${plural(addedSessions, "new session")} (all participants notified)`,
          changedSessions && `${plural(changedSessions, "changed session")}`,
          cancelledSessions && `${plural(cancelledSessions, "cancelled session")}`,
        ].filter(Boolean);
        setMessage(
          `Form updated: ${parts.join(", ")}.${
            changedSessions || cancelledSessions
              ? " Volunteers who picked a changed or cancelled session were told."
              : ""
          }`,
        );
      } else {
        setMessage(editor._id ? "Form updated." : "Form created.");
      }
      clearEditorDraft();
      setEditor(null);
      await loadForms();
    } catch (requestError) {
      setBuilderError(
        requestError.response?.data?.error || "Unable to save this form.",
      );
    } finally {
      setSaving(false);
    }
  };

  const viewResponses = async (form) => {
    setResponsesLoading(true);
    setError("");
    if (editor) persistEditorDraft(editor);
    try {
      const response = await api.get(
        `/projects/${projectId}/forms/${form._id}/responses`,
      );
      setResponseData(response.data);
      setEditor(null);
    } catch (requestError) {
      setError(
        requestError.response?.data?.error || "Unable to load form responses.",
      );
    } finally {
      setResponsesLoading(false);
    }
  };

  const responseRows = useMemo(() => {
    if (!responseData) return [];
    const fields = new Map(
      responseData.form.fields.map((field) => [String(field._id), field]),
    );
    return responseData.responses.map((response) => ({
      ...response,
      displayAnswers: response.answers
        .map((answer) => {
          const field = fields.get(String(answer.field));
          if (!field) return null;
          let value = answer.value;
          if (field.type === "availability") {
            const slots = new Map(
              field.slots.map((slot) => [String(slot._id), slot]),
            );
            value = (Array.isArray(value) ? value : [])
              .map((slotId) => {
                const slot = slots.get(String(slotId));
                return slot
                  ? `${slot.activity}: ${formatSouthAfricaDateTime(slot.startTime, "dd MMM yyyy, HH:mm")} ${SOUTH_AFRICA_TIME_LABEL}`
                  : slotId;
              })
              .join("; ");
          } else if (Array.isArray(value)) value = value.join(", ");
          return { label: field.label, value: value || "—" };
        })
        .filter(Boolean),
    }));
  }, [responseData]);

  return (
    <section className="project-form-manager">
      <div className="project-form-manager__header">
        <div>
          <h5>Availability and forms</h5>
          <p>
            Create sign-up questions and dated volunteer sessions inside WCCO.
          </p>
        </div>
        <div className="project-form-manager__header-actions">
          {hasSavedDraft && !editor && (
            <Button
              type="button"
              size="sm"
              variant="outline-primary"
              onClick={() => restoreEditorDraft(false)}>
              Resume draft
            </Button>
          )}
          <Button type="button" size="sm" onClick={startNewForm}>
            <FaCalendarPlus /> New form
          </Button>
        </div>
      </div>

      {error && <Alert>{error}</Alert>}
      {message && (
        <Alert dismissible onClose={() => setMessage("")}>
          {message}
        </Alert>
      )}
      {loading ? (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" />
        </div>
      ) : (
        <div className="project-form-manager__list">
          {forms.length === 0 ? (
            <p>No forms created yet.</p>
          ) : (
            forms.map((form) => (
              <div key={form._id} className="project-form-manager__row">
                <div>
                  <strong>{form.title}</strong>
                  <span>
                    {form.status} · {form.responseCount || 0} responses
                  </span>
                </div>
                <div>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => openFormEditor(form)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => viewResponses(form)}>
                    Responses
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {responsesLoading && (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {responseData && !responsesLoading && (
        <div className="project-form-responses">
          <div className="project-form-responses__header">
            <h6>{responseData.form.title} responses</h6>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setResponseData(null)}>
              Close
            </Button>
          </div>
          {responseRows.length ? (
            <div className="table-responsive">
              <Table className="wcco-table">
                <thead>
                  <tr>
                    <th>Volunteer</th>
                    <th>Student number</th>
                    <th>Responses</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {responseRows.map((response) => (
                    <tr key={response._id}>
                      <td>
                        {response.respondent?.name}{" "}
                        {response.respondent?.surname}
                        <small>{response.respondent?.email}</small>
                      </td>
                      <td>{response.respondent?.studentNumber || "—"}</td>
                      <td>
                        {response.displayAnswers.map((answer) => (
                          <div key={answer.label}>
                            <strong>{answer.label}:</strong> {answer.value}
                          </div>
                        ))}
                      </td>
                      <td>
                        {new Date(response.updatedAt).toLocaleString("en-ZA")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p>No responses yet.</p>
          )}
        </div>
      )}

      {editor && (
        <Form className="project-form-builder" onSubmit={saveForm}>
          <div className="project-form-builder__heading">
            <div>
              <h6>{editor._id ? "Edit form" : "New form"}</h6>
              <span className="project-form-builder__draft-status">
                {draftSavedAt
                  ? `Autosaved on this device at ${new Date(draftSavedAt).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}`
                  : "Draft autosave is on"}
              </span>
            </div>
            <div className="project-form-builder__heading-actions">
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                onClick={closeEditor}>
                Save for later
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                onClick={discardEditorDraft}>
                Discard
              </Button>
            </div>
          </div>
          {builderError && (
            <Alert variant="danger" className="project-form-builder__error">
              {builderError}
            </Alert>
          )}
          <div className="project-form-builder__settings">
            <Form.Group>
              <Form.Label>Form title</Form.Label>
              <Form.Control
                value={editor.title}
                onChange={(event) =>
                  setEditor({ ...editor, title: event.target.value })
                }
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={editor.status}
                onChange={(event) =>
                  setEditor({ ...editor, status: event.target.value })
                }>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="closed">Closed</option>
              </Form.Select>
              <Form.Text muted>
                {editor.status === "published"
                  ? "Volunteers can see and answer this form."
                  : editor.status === "closed"
                    ? "Volunteers can no longer see this form."
                    : "Drafts are hidden from volunteers. Choose Published when it is ready."}
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Closing date (optional)</Form.Label>
              <Form.Control
                type="datetime-local"
                value={editor.closesAt || ""}
                onChange={(event) =>
                  setEditor({ ...editor, closesAt: event.target.value })
                }
              />
            </Form.Group>
          </div>
          <Form.Group className="mt-3">
            <Form.Label>Instructions</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={editor.description || ""}
              onChange={(event) =>
                setEditor({ ...editor, description: event.target.value })
              }
            />
          </Form.Group>

          <div className="project-form-builder__availability-action">
            <div>
              <strong>Form building shortcuts</strong>
              <span>Add selectable dates or answer choices directly.</span>
            </div>
            <div className="project-form-builder__shortcut-buttons">
              <Button type="button" onClick={addAvailableSlot}>
                <FaCalendarPlus /> Add available slot
              </Button>
              <Button
                type="button"
                variant="outline-primary"
                onClick={addAnswerChoice}>
                <FaPlus /> Add answer choice
              </Button>
            </div>
          </div>

          <div className="project-form-builder__fields">
            {editor.fields.map((field, fieldIndex) => (
              <article key={field._key} className="project-form-builder__field">
                <div className="project-form-builder__field-head">
                  <strong>Question {fieldIndex + 1}</strong>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    aria-label="Remove question"
                    onClick={() =>
                      setEditor({
                        ...editor,
                        fields: editor.fields.filter(
                          (_, index) => index !== fieldIndex,
                        ),
                      })
                    }>
                    <FaTrash />
                  </Button>
                </div>
                <div className="project-form-builder__field-grid">
                  <Form.Group>
                    <Form.Label>Question</Form.Label>
                    <Form.Control
                      value={field.label}
                      onChange={(event) =>
                        updateField(fieldIndex, { label: event.target.value })
                      }
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Type</Form.Label>
                    <Form.Select
                      value={field.type}
                      onChange={(event) =>
                        changeFieldType(fieldIndex, event.target.value)
                      }>
                      {fieldTypes.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <Form.Group className="mt-2">
                  <Form.Label>Help text (optional)</Form.Label>
                  <Form.Control
                    value={field.helpText || ""}
                    onChange={(event) =>
                      updateField(fieldIndex, { helpText: event.target.value })
                    }
                  />
                </Form.Group>
                <Form.Check
                  className="mt-2"
                  type="switch"
                  label="Required question"
                  checked={field.required}
                  onChange={(event) =>
                    updateField(fieldIndex, { required: event.target.checked })
                  }
                />

                {["single_choice", "multi_choice"].includes(field.type) && (
                  <div className="project-choice-builder">
                    <div className="project-choice-builder__heading">
                      <div>
                        <strong>Answer choices</strong>
                        <span>
                          {
                            getChoiceOptions(field).filter((option) =>
                              option.trim(),
                            ).length
                          }{" "}
                          added
                        </span>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-primary"
                        onClick={() => addChoice(fieldIndex)}>
                        <FaPlus /> Add answer choice
                      </Button>
                    </div>
                    {getChoiceOptions(field).map((option, optionIndex) => (
                      <div
                        key={`${field._key}-option-${optionIndex}`}
                        className="project-choice-builder__row">
                        <Form.Control
                          aria-label={`Answer choice ${optionIndex + 1}`}
                          placeholder={`Option ${optionIndex + 1}`}
                          value={option}
                          onChange={(event) =>
                            updateChoice(
                              fieldIndex,
                              optionIndex,
                              event.target.value,
                            )
                          }
                          required
                        />
                        <Button
                          type="button"
                          variant="outline-secondary"
                          aria-label={`Remove answer choice ${optionIndex + 1}`}
                          onClick={() => removeChoice(fieldIndex, optionIndex)}
                          disabled={getChoiceOptions(field).length <= 2}>
                          <FaTrash />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {field.type === "availability" && (
                  <div className="project-slot-builder">
                    <div className="project-slot-builder__heading">
                      <div>
                        <strong>Available slots</strong>
                        <span>{field.slots?.length || 0} added</span>
                      </div>
                      <div className="project-slot-builder__heading-actions">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline-primary"
                          onClick={() =>
                            setRepeatFieldKey(repeatFieldKey === field._key ? "" : field._key)
                          }>
                          <FaRedo /> Repeat sessions
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline-primary"
                          onClick={() => addSlot(fieldIndex)}>
                          <FaPlus /> Add available slot
                        </Button>
                      </div>
                    </div>
                    {repeatFieldKey === field._key && (
                      <RepeatSessionsPanel
                        existingSlots={field.slots || []}
                        onAdd={(newSlots) => addSlots(fieldIndex, newSlots)}
                        onClose={() => setRepeatFieldKey("")}
                      />
                    )}
                    <Form.Text muted>
                      Volunteers see every session from every availability
                      question in one list, sorted by date — keep adding
                      dates here rather than adding new questions.
                    </Form.Text>
                    {!field.slots?.length && (
                      <p className="project-slot-builder__empty">
                        No slots yet. Add the first date and time students can
                        select.
                      </p>
                    )}
                    {(field.slots || []).map((slot, slotIndex) => (
                      <div
                        key={slot._key}
                        className="project-slot-builder__row">
                        <Form.Group>
                          <Form.Label>Activity</Form.Label>
                          <Form.Control
                            placeholder="Diner"
                            value={slot.activity}
                            onChange={(event) =>
                              updateSlot(fieldIndex, slotIndex, {
                                activity: event.target.value,
                              })
                            }
                            required
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Slot label</Form.Label>
                          <Form.Control
                            placeholder="Monday morning"
                            value={slot.label || ""}
                            onChange={(event) =>
                              updateSlot(fieldIndex, slotIndex, {
                                label: event.target.value,
                              })
                            }
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Starts</Form.Label>
                          <Form.Control
                            type="datetime-local"
                            value={slot.startTime || ""}
                            onChange={(event) => {
                              const startTime = event.target.value;
                              const suggestedEndTime = suggestSlotEndTime(startTime);
                              const currentEnd = parseSouthAfricaDateTimeLocal(slot.endTime);
                              const nextStart = parseSouthAfricaDateTimeLocal(startTime);
                              updateSlot(fieldIndex, slotIndex, {
                                startTime,
                                ...(!currentEnd || (nextStart && currentEnd <= nextStart)
                                  ? { endTime: suggestedEndTime }
                                  : {}),
                              });
                            }}
                            required
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Ends</Form.Label>
                          <Form.Control
                            type="datetime-local"
                            value={slot.endTime || ""}
                            min={slot.startTime || undefined}
                            isInvalid={slotHasInvalidRange(slot)}
                            onChange={(event) =>
                              updateSlot(fieldIndex, slotIndex, {
                                endTime: event.target.value,
                              })
                            }
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            Choose an end date and time later than the start.
                          </Form.Control.Feedback>
                          <Form.Text>New slots default to one hour after the start.</Form.Text>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Capacity (0 = unlimited)</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            value={slot.capacity || 0}
                            onChange={(event) =>
                              updateSlot(fieldIndex, slotIndex, {
                                capacity: event.target.value,
                              })
                            }
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Location</Form.Label>
                          <Form.Control
                            value={slot.location || ""}
                            onChange={(event) =>
                              updateSlot(fieldIndex, slotIndex, {
                                location: event.target.value,
                              })
                            }
                          />
                        </Form.Group>
                        <div className="project-slot-builder__row-actions">
                          <Button
                            type="button"
                            variant="outline-secondary"
                            aria-label="Copy this slot to the same time next week"
                            title="Copy to next week"
                            disabled={!slot.startTime || !slot.endTime}
                            onClick={() => copySlotToNextWeek(fieldIndex, slotIndex)}>
                            <FaClone />
                          </Button>
                          <Button
                            type="button"
                            variant="outline-secondary"
                            aria-label="Remove slot"
                            onClick={() => removeSlot(fieldIndex, slotIndex)}>
                            <FaTrash />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>

          <div className="project-form-builder__add">
            <Form.Select
              value={newFieldType}
              onChange={(event) => setNewFieldType(event.target.value)}>
              {fieldTypes.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Select>
            <Button
              type="button"
              variant="outline-primary"
              onClick={addField}
              disabled={editor.fields.length >= MAX_FORM_FIELDS}>
              <FaPlus /> Add question
            </Button>
          </div>
          <Form.Text muted>
            {editor.fields.length} of {MAX_FORM_FIELDS} questions used. One
            availability question can hold any number of dates — add them with
            "Add available slot".
          </Form.Text>
          <div className="project-form-builder__actions">
            <Button type="submit" disabled={saving}>
              {saving
                ? "Saving…"
                : editor.status === "published"
                  ? "Publish form"
                  : editor.status === "closed"
                    ? "Save closed form"
                    : "Save draft"}
            </Button>
          </div>
        </Form>
      )}
    </section>
  );
};

export default ProjectFormManager;
